package puzzlekey

import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToInt

/** Width over height of the guide overlay: the T04 recorder's "landscape 7:5", which every corpus frame used. */
const val GUIDE_ASPECT = 1.4

/** Share of the video's width (or height, on a wide screen) the guide takes, as in the recorder. */
const val GUIDE_SHARE = 0.86

/**
 * The guide as the corpus saved it: 881 × 629 px. A Scan draws the guide's video pixels into a canvas
 * of this size, so the model sees the picture at the scale Enrollment saw it (ADR-0009 §2).
 */
val SCAN_FRAME = Rect(0, 0, 881, (881 / GUIDE_ASPECT).roundToInt())

/** A rectangle in CSS pixels. */
data class Box(val x: Double, val y: Double, val w: Double, val h: Double)

/** Where the guide overlay sits on a [viewWidth] × [viewHeight] video element: centred, [GUIDE_SHARE] of it, [GUIDE_ASPECT]. */
fun guideOnScreen(viewWidth: Double, viewHeight: Double): Box {
    var w = viewWidth * GUIDE_SHARE
    var h = w / GUIDE_ASPECT
    if (h > viewHeight * GUIDE_SHARE) {
        h = viewHeight * GUIDE_SHARE
        w = h * GUIDE_ASPECT
    }
    return Box((viewWidth - w) / 2, (viewHeight - h) / 2, w, h)
}

/**
 * The video pixels under [guide] (CSS px over the video element), for a video shown with
 * `object-fit: cover` on a [viewWidth] × [viewHeight] element. Clamped to the video.
 */
fun guideInVideo(viewWidth: Double, viewHeight: Double, videoWidth: Int, videoHeight: Int, guide: Box): Rect {
    val scale = max(viewWidth / videoWidth, viewHeight / videoHeight)
    val offX = (viewWidth - videoWidth * scale) / 2
    val offY = (viewHeight - videoHeight * scale) / 2
    val left = max(0, ((guide.x - offX) / scale).roundToInt())
    val top = max(0, ((guide.y - offY) / scale).roundToInt())
    val right = min(videoWidth, ((guide.x + guide.w - offX) / scale).roundToInt())
    val bottom = min(videoHeight, ((guide.y + guide.h - offY) / scale).roundToInt())
    return Rect(left, top, right - left, bottom - top)
}
